<template>
  <div
    class="backtest-shell app-shell"
    :style="{
      marginTop: headerHeight + 'px',
      minHeight: 'calc(100vh - ' + headerHeight + 'px)'
    }"
  >
    <aside class="backtest-sidebar" :style="{ top: headerHeight + 'px' }">
      <div class="sidebar-title">
        <i class="el-icon-data-analysis"></i>
        <div>
          <span class="sidebar-eyebrow">BACKTEST</span>
          <h1>Algorithm Test</h1>
        </div>
      </div>

      <!-- Create Test Form -->
      <BackTestForm @test-created="handleTestCreated" />

      <!-- Test History -->
      <BackTestHistory
        :tests="tests"
        :selectedTestId="selectedTestId"
        :loading="loading"
        :totalCount="totalCount"
        @select-test="handleSelectTest"
        @load-more="handleLoadMore"
      />
    </aside>

    <main class="backtest-main app-main">
      <div class="backtest-page">
        <AppBreadcrumb
          root="Backtest"
          :current="currentPageTitle"
          :rootClickable="false"
        />
        <BackTestDetail
          v-if="selectedTestId"
          :key="selectedTestId"
          :testId="selectedTestId"
          @refresh="loadTests"
        />
        <div v-else class="empty-state">
          <i class="el-icon-data-analysis"></i>
          <strong>No test selected</strong>
          <p>Select a test from the history to view its results.</p>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import BackTestForm from '@/components/backtest/BackTestForm.vue'
import BackTestHistory from '@/components/backtest/BackTestHistory.vue'
import BackTestDetail from '@/components/backtest/BackTestDetail.vue'
import BackTestApiService from '@/services/BackTestApiService'
import AppBreadcrumb from '@/components/AppBreadcrumb'
import headerHeight from '@/mixins/headerHeight'
import { jobTypes } from '@/const'

export default {
  name: 'BackTest',
  mixins: [headerHeight],
  components: {
    BackTestForm,
    BackTestHistory,
    BackTestDetail,
    AppBreadcrumb
  },
  data() {
    return {
      tests: [],
      selectedTestId: null,
      loading: false,
      refreshInterval: null,
      totalCount: 0,
      currentPage: 1,
      pageSize: 100
    }
  },
  computed: {
    currentPageTitle() {
      return this.selectedTestId ? `Test #${this.selectedTestId}` : 'Overview'
    }
  },
  mounted() {
    this.loadTests()
    // Auto-refresh test list every 30 seconds
    this.refreshInterval = setInterval(() => {
      this.loadTests()
    }, 30000)
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },
  methods: {
    async loadTests(page = 1) {
      this.loading = true
      try {
        const response = await BackTestApiService.getBackTestList({
          page: page,
          pageSize: this.pageSize,
          jobType: [
            jobTypes.backTestingVolumeEstimation,
            jobTypes.backTestingRedAlert,
            jobTypes.backTestingNewPad
          ].join(',')
        })

        const data = response.data
        if (page === 1) {
          this.tests = data.Items || []
        } else {
          this.tests = [...this.tests, ...(data.Items || [])]
        }

        this.totalCount = data.TotalCount || 0
        this.currentPage = data.CurrentPage || 1

        // Auto-select the most recent test if none selected
        if (!this.selectedTestId && this.tests.length > 0) {
          this.selectedTestId = this.tests[0].Id
        }
      } finally {
        this.loading = false
      }
    },
    handleTestCreated(testId) {
      this.loadTests()
      this.selectedTestId = testId
    },
    handleSelectTest(testId) {
      this.selectedTestId = testId
    },
    handleLoadMore() {
      this.loadTests(this.currentPage + 1)
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/css/theme.scss';

.backtest-sidebar {
  position: fixed;
  left: 0;
  bottom: 0;
  z-index: 10;
  width: 340px;
  padding: 20px 18px 16px;
  overflow: hidden;
  background: var(--sidebar);
  display: flex;
  flex-direction: column;
  color: var(--sidebar-t);
  border-right: 1px solid rgba(255, 255, 255, 0.06);
}

.sidebar-title {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);

  > i {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    color: #fff;
    background: var(--sidebar-a);
    font-size: 18px;
  }

  .sidebar-eyebrow {
    display: block;
    margin-bottom: 2px;
    color: var(--sidebar-t2);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
  }

  h1 {
    margin: 0;
    color: var(--sidebar-t);
    font-size: 17px;
    font-weight: 600;
    line-height: 1.2;
  }
}

.backtest-main {
  margin-left: 340px;
  min-height: inherit;
  background: var(--bg3);
}

.backtest-page {
  width: 100%;
  min-width: 0;
  padding: 20px 24px 32px;
}

::v-deep .backtest-form {
  flex-shrink: 0;
}

::v-deep .backtest-history {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  margin-right: -8px;
  padding-right: 8px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 420px;
  padding: 40px;
  color: var(--text3);
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 8px;

  i {
    margin-bottom: 14px;
    color: var(--border2);
    font-size: 42px;
  }

  strong {
    margin-bottom: 4px;
    color: var(--text2);
    font-size: 15px;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 13px;
  }
}

@media (max-width: 1100px) {
  .backtest-sidebar {
    width: 300px;
  }

  .backtest-main {
    margin-left: 300px;
  }
}

@media (max-width: 820px) {
  .backtest-shell {
    display: block;
  }

  .backtest-sidebar {
    position: static;
    width: 100%;
    max-height: none;
  }

  .backtest-main {
    margin-left: 0;
  }

  .backtest-page {
    padding: 16px;
  }
}
</style>
